import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { ChevronDown } from 'lucide-react';
import { Button, type ButtonSize } from './Button';
import './MoreMenu.css';

export interface MoreMenuItem {
  label: string;
  icon?: ReactNode;
  /** Descrierea de sub etichetă — de ce ai apăsa, nu ce face butonul. */
  hint?: string;
  onSelect: () => void;
  disabled?: boolean;
  /** Se desenează în roșu: golește, șterge, refuză. */
  danger?: boolean;
}

/**
 * Acțiunile rare, strânse sub un singur buton.
 *
 * O bară cu opt butoane de aceeași greutate spune că toate opt sunt la fel de
 * importante, deci niciunul nu e — pe Stocuri, „Scanează" (de zeci de ori pe
 * zi) stătea lângă „Praguri din consum" (de două ori pe an), la fel desenate.
 * Ce rămâne afară e ce se apasă zilnic; restul intră aici și își păstrează
 * explicația, care în bară nu încăpea decât ca `title` — adică doar pentru
 * cine are mouse și rabdă o secundă.
 *
 * Se închide la Escape, la un clic în afară și după ce s-a ales ceva. Nu
 * încearcă să fie un `menu` ARIA complet cu navigare pe săgeți: sunt butoane
 * într-o listă, deci Tab le parcurge oricum, iar un rol pe care nu-l
 * implementezi până la capăt e mai rău decât unul pe care nu-l pretinzi.
 */
export function MoreMenu({
  items,
  label = 'Mai multe',
  size = 'sm',
  align = 'end',
}: {
  items: MoreMenuItem[];
  label?: string;
  size?: ButtonSize;
  align?: 'start' | 'end';
}) {
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);
  const id = useId();

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!wrap.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const usable = items.filter((i) => !i.disabled);
  if (usable.length === 0) return null;

  return (
    <div className="mmenu" ref={wrap}>
      <Button
        size={size}
        variant="ghost"
        aria-haspopup="true"
        aria-expanded={open}
        aria-controls={open ? id : undefined}
        iconAfter={<ChevronDown size={14} />}
        onClick={() => setOpen((v) => !v)}
      >
        {label}
      </Button>
      {open ? (
        <div className={`mmenu__list mmenu__list--${align}`} id={id}>
          {usable.map((it) => (
            <button
              key={it.label}
              type="button"
              className={`mmenu__item ${it.danger ? 'mmenu__item--danger' : ''}`}
              onClick={() => {
                setOpen(false);
                it.onSelect();
              }}
            >
              {it.icon ? (
                <span className="mmenu__icon" aria-hidden>
                  {it.icon}
                </span>
              ) : null}
              <span className="mmenu__text">
                <span className="mmenu__label">{it.label}</span>
                {it.hint ? <span className="mmenu__hint">{it.hint}</span> : null}
              </span>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
